import { stringify } from 'querystring'
import moment from 'moment-timezone'
import * as AT from './_types'
import {
  chooseCurrentConversation,
  isCurrConversationEmail,
  resetCurrentConversation,
  setCurrConvoMsgType,
} from './current'
import {
  updatePatientConversationIds,
  addContactInfoToPatient,
} from './selected-patient'
import fetchApi from '../../services/fetchApi'

const setFetchingConvoState = (listName, nextVal) => ({
  type: nextVal ? AT.CONVERSATIONS_FETCHING : AT.CONVERSATIONS_FETCHED,
  listName,
})

const trackConversationPages = (listName, nextPageNum, totalPages) => ({
  type: AT.CONVERSATIONS_PAGE_NUM,
  listName,
  nextPageNum,
  totalPages,
})

export const setCreatingConvoState = receivedResponse => ({
  type: AT.CONVERSATIONS_CREATING,
  receivedResponse,
})

const addNewlyCreatedConversationToIds = conversation => ({
  type: AT.CONVERSATIONS_ADD_CREATED,
  conversation,
})

export const addMediaToMessage = (media, conversationId) => dispatch =>
  dispatch({
    type: AT.MESSAGE_ADD_MEDIA,
    media,
    conversationId,
  })

export const addSubjectToConversation = subject => ({
  type: AT.CONVERSATIONS_UPDATE_SUBJECT,
  subject,
})

export const updateMessageStatus = (textId, status) => dispatch =>
  dispatch({
    type: AT.MESSAGE_UPDATE_STATUS,
    textId,
    status,
  })

const listNameFromConversationStatus = activeStatus =>
  activeStatus === 'active' ? 'activeConvos' : 'allConvos'

// fetch all the messages associated with a given conversation
export const getMessagesForConversation = (conversationId) => dispatch => {
  dispatch({ type: AT.MESSAGE_LOADING, conversationId })

  return fetchApi(`/conversations/${conversationId}`, 'GET').then(
    conversation => {
      dispatch({ type: AT.REMOVE_UNSEEN_CONVO, convoId: conversationId })
      const { messages } = conversation
      const listOfIds = [] // ids of messages that belong to the conversation

      const messagesById = messages.reduceRight((byId, msg) => {
        const { id } = msg
        byId[id] = msg
        listOfIds.push(id)

        return byId
      }, {})

      dispatch({
        type: AT.MESSAGES_RECEIVE_IN_BULK,
        messagesById,
      })

      dispatch({
        type: AT.MESSAGES_ADD_TO_CONVO,
        conversationId,
        listOfIds,
      })

      dispatch({
        type: AT.CONVERSATIONS_UPDATE_ONE,
        conversationId,
        seen: true,
        subject: conversation.subject,
      })

      dispatch({ type: AT.MESSAGE_LOADED, conversationId })

      return messages
    },
  )
}

export function updateConversationSearch(searchTerm) {
  return {
    type: AT.CONVERSATIONS_UPDATE_SEARCH,
    search: searchTerm,
  }
}

export function updateConversationMessageType(messageType) {
  return {
    type: AT.CONVERSATIONS_UPDATE_MSG_TYPE,
    messageType,
  }
}

const fetchLocations = () => async dispatch => {
  try {
    const locs = await fetchApi('/account/locations')
    dispatch(setLocations(locs))
  } catch (err) {
    console.error(err)
  }
}

function setLocations(locations) {
  return {
    type: AT.CONVERSATIONS_RECEIVE_LOCATIONS,
    locations,
  }
}

const deleteConvo = convoId => ({
  type: AT.CONVERSATIONS_DELETE,
  idToDelete: convoId,
  listName: listNameFromConversationStatus('all'),
})

export const archiveConvo = (active, convoId) => dispatch => {
  const postBody = JSON.stringify({ active })
  return fetchApi(`/conversations/${convoId}`, 'PATCH', {
    body: postBody,
  }).then(response => {
    const { active, convo_id: convoId } = response
    const activeListName = listNameFromConversationStatus('active')
    const idsList = [convoId]

    if (active) {
      dispatch({
        type: AT.CONVERSATIONS_ACTIVATE,
        convoId,
        listName: activeListName,
        idsList,
      })
    } else {
      dispatch({
        type: AT.CONVERSATIONS_INACTIVATE,
        idToInactivate: convoId,
        listName: activeListName,
      })
    }

    return response
  })
}

/**
 * @param activeStatus, {string} Status of conversation to request.
 *   Can be one of ['active', 'all'].
 * @param chooseActiveConvo, {bool} Automatically choose a conversation to focus
 *   on after receiving response?
 */
export const getConversations =
  (
    { activeStatus = 'all', chooseActiveConvo = true } = {},
    queryParams = '',
    page = 1,
    fetchController = null
  ) =>
  (dispatch, getState) => {
    const listName = listNameFromConversationStatus(activeStatus)
    dispatch(setFetchingConvoState(listName, true))
    dispatch(fetchLocations())

    const params = stringify({ ...queryParams, page })
    const { isBlastMsg, id: currConvoId } = getState().current.conversation

    return fetchApi(
      `/conversations?active_status=${activeStatus}&${params}`,
      'GET',
      { fetchController }
    )
      .then(({ conversations, total_pages: totalPages }) => {
        const {
          account: {
            user: { phoneNumbers },
          },
        } = getState()
        dispatch(trackConversationPages(listName, page, totalPages))

        return conversations.reduce(
          (result, conversation) => {
            const {
              id,
              messages_type: msgType,
              practice_phone: practicePhone,
            } = conversation

            // make sure phone number hasn't been deleted, otherwise texts will try to send
            // from this practicePhone number. this also skips storing this conversation
            if (msgType === 'text' && !phoneNumbers.includes(practicePhone)) {
              return result
            }

            result.byId[id] = conversation

            // Find first conversation where 'active' property is true.
            if (!result.activeConvoId && conversation.active) {
              result.activeConvoId = id
            }

            result.idsList.push(id)

            return result
          },
          {
            byId: {},
            idsList: [],
            activeConvoId: null,
          },
        )
      })
      .then(shapedResults => {
        // FIX: this dispatch will clear the unknown convo
        // if 'Text Unknown Number' is taking user to archive
        // tab for the first time (i.e. pre-fetch)
        dispatch({
          type: AT.CONVERSATIONS_RECEIVE,
          listName,
          conversations: shapedResults.byId,
          idsList: shapedResults.idsList,
        })

        return shapedResults
      })
      .then(shapedResults => {
        if (chooseActiveConvo && shapedResults.idsList.length > 0) {
          if (!shapedResults.activeConvoId) {
            // set to first conversation in case no conversations are active
            shapedResults.activeConvoId = shapedResults.idsList[0]
          }

          dispatch(deleteConvo(-1))
          dispatch(chooseCurrentConversation(shapedResults.activeConvoId))
          dispatch(isCurrConversationEmail())
          if (shapedResults.activeConvoId !== -1) {
            dispatch(getMessagesForConversation(shapedResults.activeConvoId))
          }
        } else if (
          shapedResults.idsList.length <= 0 &&
          currConvoId !== -1 &&
          !isBlastMsg
        ) {
          dispatch(resetCurrentConversation())
        }

        dispatch(setFetchingConvoState(listName, false))
      })
  }

export const startNewConversation = (messagesType, patientId) => dispatch => {
  const url = `/contact_info?messages_type=${messagesType}&patient_id=${patientId}`
  return fetchApi(url, 'GET')
    .then(res => {
      const { contact_info: contactInfo, location_phone: locationPhone } = res
      if (contactInfo) {
        dispatch(setCreatingConvoState(true))
        dispatch(addContactInfoToPatient(contactInfo, locationPhone))
        dispatch(setCurrConvoMsgType(messagesType))
        return { success: true }
      }
      const contactMethod =
        messagesType === 'email' ? 'email address' : 'phone number'
      return { error: `No valid ${contactMethod} found for this patient` }
    })
    .catch(err => {
      console.error(err)
      return {
        error:
          'There was an error creating a new message, please try again later',
      }
    })
}

export const fetchNextConversationPage =
  (nextPageNum, activeStatus = 'all', queryParams = '') =>
  (dispatch, getState) => {
    const qsParams = stringify({
      ...queryParams,
      page: nextPageNum,
      active_status: activeStatus,
    })
    const listName = listNameFromConversationStatus(activeStatus)
    dispatch(setFetchingConvoState(listName, true))
    dispatch(deleteConvo(-1))

    return fetchApi(`/conversations?${qsParams}`, 'GET').then(
      ({ conversations: addedConversations, total_pages: totalPages }) => {
        const idsList = [] // collect ids for rendering convos

        const newerConversations = {}
        const {
          conversations: stateConvos,
          account: {
            user: { phoneNumbers },
          },
        } = getState()
        const currConversations = stateConvos.byId
        const currIdsList = stateConvos[listName].idsList

        addedConversations.forEach(convo => {
          const {
            id: convoId,
            messages_type: msgType,
            practice_phone: practicePhone,
          } = convo

          // make sure phone number hasn't been deleted, otherwise texts will try to send
          // from this practicePhone number. this also skips storing this conversation
          if (msgType === 'text' && !phoneNumbers.includes(practicePhone)) {
            return
          }
          if (!currConversations.hasOwnProperty(convoId)) {
            newerConversations[convoId] = convo
          }

          if (!currIdsList.has(convoId)) {
            idsList.push(convoId)
          }
        })

        dispatch({
          type: AT.CONVERSATIONS_RECEIVE_NEXT_PAGE,
          conversations: newerConversations,
          listName,
          idsList,
        })

        // increment current page number (sets up request for next page of convos)
        dispatch(trackConversationPages(listName, nextPageNum, totalPages))
        dispatch(setFetchingConvoState(listName, false))
      },
    )
  }

export const storeConversationDetails = conversation => dispatch => {
  const { id } = conversation
  const idsList = []
  const byId = {}

  byId[id] = conversation
  idsList.push(id)

  if (conversation.active) {
    const activeListName = listNameFromConversationStatus('active')
    dispatch({
      type: AT.CONVERSATIONS_RECEIVE_NOTIFICATION,
      listName: activeListName,
      conversations: byId,
      idsList,
    })
  }
  const allListName = listNameFromConversationStatus('all')
  dispatch({
    type: AT.CONVERSATIONS_RECEIVE_NOTIFICATION,
    listName: allListName,
    conversations: byId,
    idsList,
  })

  return Promise.resolve()
}

export const addMessagesToConversationFromNotification =
  (messages, conversation) => (dispatch, getState) => {
    const listOfIds = []
    const messagesById = messages.reduceRight((byId, msg) => {
      const { id } = msg
      byId[id] = msg
      listOfIds.push(id)

      return byId
    }, {})

    const { id } = conversation

    dispatch({
      type: AT.MESSAGES_RECEIVE_IN_BULK,
      messagesById,
    })

    dispatch({
      type: AT.MESSAGES_ADD_TO_CONVO,
      conversationId: id,
      listOfIds,
    })

    const msgTypeFilter = getState().conversations.messageType
    if (['all', conversation.messages_type].includes(msgTypeFilter)) {
      dispatch(storeConversationDetails(conversation))
    }

    return Promise.resolve()
  }

// whether or not it was success, signal completion of request/response
export const markMessageComplete = conversationId => ({
  type: AT.MESSAGE_SEND_DONE,
  conversationId,
})

export const sendMessage =
  ({
    isEmailMsg,
    conversationId,
    bodyText,
    newConvo,
    media,
    unknownNumber,
    activeLocationId,
  }) =>
  async (dispatch, getState) => {
    const {
      messages: messagesState,
      selectedPatient: patientState,
      conversations,
      account,
    } = getState()
    const apiEndpoint = isEmailMsg ? '/emails' : '/texts'
    const newConvoSubject = newConvo
      ? conversations.newConversationSubject
      : null
    const message = newConvo
      ? formatNewConvoMessage(
          bodyText,
          newConvoSubject,
          isEmailMsg,
          patientState,
          media,
        )
      : formatExistingConvoMessage(
          bodyText,
          conversationId,
          isEmailMsg,
          messagesState,
          media,
          unknownNumber,
          activeLocationId,
          account,
        )
    if (!isEmailMsg && newConvo) {
      const locationPhone = patientState.patient.location_phone_number
      message.From = locationPhone
    }
    // send patient's location for dso practice email messaging
    if (activeLocationId && isEmailMsg && newConvo) {
      message.patient_location_id = patientState.patient.location_id
    }
    const postBody = buildPostBody(isEmailMsg, message)

    // for 'instant' rendering, pre-fill some attributes needed for local state's stake.
    message.to_patient = true
    // TODO: regarding the 'instant' rendering:
    // How to index message without a DB id? perhaps keep in a temporary area,
    // then index after getting an API response back?

    dispatch({ type: AT.MESSAGE_SENDING, conversationId })

    return fetchApi(apiEndpoint, 'POST', { body: postBody }).then(
      msgResponse => {
        msgResponse.media = !!media
        if (conversationId === -1) {
          dispatch({
            type: AT.MESSAGE_ADD_MEDIA,
            media: null,
            conversationId,
          })
        }
        dispatch(markMessageComplete(conversationId))
        // 'sent_at' is null which displays as "Invalid Date" on the front end.
        // This adds a temporary timestamp to the message for better UI that is
        // later overwritten by the database.
        msgResponse.sent_at = msgResponse.sent_at || moment().format()

        const updatedConversationId = newConvo
          ? msgResponse.conversation_id
          : conversationId
        if (newConvo) {
          // add email subject to conversation or patient phone number to text message
          if (isEmailMsg) {
            msgResponse.conversation.subject = newConvoSubject
          } else {
            msgResponse.patient_phone_number = msgResponse.to
          }
          const convosIdKey = `${msgResponse.conversation.messages_type}ConversationIds`
          dispatch(
            updatePatientConversationIds(updatedConversationId, convosIdKey),
          )
          dispatch(addNewlyCreatedConversationToIds(msgResponse.conversation))
          dispatch(chooseCurrentConversation(updatedConversationId))
        } else {
          // `conversation_id` is not correct for emails in development environment.
          // It depends on sendgrid to assign a conversation_id, but is OK for text
          // messages. In the meantime, manually override `message.conversation_id`.
          msgResponse.conversation_id = conversationId
        }

        // This will always be true when sending a message. But if API is messed
        // up, shim it back.
        if (!msgResponse.to_patient) {
          msgResponse.to_patient = true
        }

        if (conversationId !== -1) {
          dispatch({
            type: AT.MESSAGE_RECEIVE_ONE,
            message: msgResponse,
          })

          dispatch({
            type: AT.MESSAGE_APPEND_TO_LIST,
            conversationId: updatedConversationId,
            messageId: msgResponse.id,
          })
        } else {
          dispatch(deleteConvo(conversationId))
          dispatch(getConversations({ activeStatus: 'all' }))
        }

        return msgResponse
      },
    )
  }

function formatNewConvoMessage(
  bodyText,
  newConvoSubject,
  isEmailMsg,
  patientState,
  media,
) {
  const contactInfo = patientState.patient.preferred_contact_info
  return isEmailMsg
    ? setupEmailMessage(contactInfo, newConvoSubject, null, bodyText)
    : setupTextMessage(contactInfo, bodyText, media)
}

function formatExistingConvoMessage(
  bodyText,
  conversationId,
  isEmailMsg,
  messagesState,
  media,
  unknownNumber,
  activeLocationId,
  account,
) {
  if (conversationId === -1) {
    return setupNewUnknownNumberText(
      unknownNumber,
      bodyText,
      media,
      activeLocationId,
    )
  }
  const referenceMsgId =
    messagesState.dataByConversation[conversationId].messageIds[0]
  const {
    patient_phone_number: patientPhone,
    practice_phone_number: practicePhone,
    subject,
    to,
    from,
  } = messagesState.byId[referenceMsgId]
  if (isEmailMsg) {
    // Validate the 'to' and 'from' fields
    const isToValid = !!to;
    const isFromValid = !!from;
    const allLocationEmails = account.user.locations.flatMap(
      location => [location.office_email, location.forwarding_email].filter(Boolean)
    )
    const uniqueLocationEmails = new Set(allLocationEmails)
    // Check if 'to' is in the set of unique location emails
    const isToLocationEmail = uniqueLocationEmails.has(to);
    // If 'to' and 'from' are both valid and 'to' is a location email, use 'from' as the recipient
    // Otherwise, use 'to' as the recipient

    if (account.user.practiceName === 'Cloud9 Test Practice'){
      console.log('account.user.practiceName', account.user.practiceName)
      console.log('allLocationEmails', allLocationEmails)
      console.log('uniqueLocationEmails', uniqueLocationEmails)
      console.log('to', to)
      console.log('isToValid', isToValid)
      console.log('from', from)
      console.log('isFromValid', isFromValid)
      console.log('isToLocationEmail', isToLocationEmail)
      console.log('recipientEmail', isToValid && isFromValid && isToLocationEmail ? from : to)
      let fromCopy = from
      if(emailAddressEncoded(fromCopy)) {
        fromCopy = decodeLocalMailbox(fromCopy)
      }
      console.log('from decoded', fromCopy)
      console.log('recipientEmailDecoded', isToValid && isFromValid && isToLocationEmail ? fromCopy : to)
      function emailAddressEncoded(emailAddress) {
        let match = emailAddress.match(/^(.*?)(?=@)/);
        
        if (!match) {
            return false;
        }
        
        return match[1].includes('_at_') || match[1].includes('_dot_');
      }

      function formattedTo(to) {
          let match = to.match(/<(.*?)>/);
          
          if (match) {
              return match[1];
          } else {
              return to;
          }
      }

      function decodeLocalMailbox(to) {
          let formatted = formattedTo(to);
          let match = formatted.match(/^(.*?)\@/);

          if (match) {
              return match[1].replace('_at_', '@').replace('_dot_', '.');
          } else {
              return '';
          }
      }

      const recipientEmail = isToValid && isFromValid && isToLocationEmail ? fromCopy : to
      return setupEmailMessage(recipientEmail, subject, conversationId, bodyText)
    }

    return setupEmailMessage(to, subject, conversationId, bodyText)
  } else {
    return setupExistingConvoTextMessage(
      patientPhone,
      practicePhone,
      bodyText,
      media,
    )
  }
}

function setupEmailMessage(email, subject, conversationId, bodyText) {
  // back-end API looks for lower-cased params
  const emailBody = {
    to: email,
    subject,
    body: bodyText,
  }
  if (conversationId) {
    emailBody.conversation_id = conversationId
  }
  return emailBody
}

function setupTextMessage(patientPhone, bodyText, media = null) {
  // back-end API looks for UPPER-CASED params (twilio requirement)
  return {
    To: patientPhone,
    Body: bodyText,
    body: bodyText, // duplicating key/value for immediate rendering
    media,
    // TODO: what is the param `SmsSid` all about?
  }
}

function setupExistingConvoTextMessage(
  patientPhone,
  practicePhone,
  bodyText,
  media = null,
) {
  // back-end API looks for UPPER-CASED params (twilio requirement)
  return {
    To: patientPhone,
    From: practicePhone,
    Body: bodyText,
    body: bodyText, // duplicating key/value for immediate rendering
    media,
  }
}

function setupNewUnknownNumberText(
  unknownNumber,
  bodyText,
  media,
  activeLocationId,
) {
  // back-end API looks for UPPER-CASED params (twilio requirement)
  return {
    To: unknownNumber,
    Body: bodyText,
    body: bodyText, // duplicating key/value for immediate rendering
    media,
    unknown: true,
    activeLocationId,
  }
}

function buildPostBody(isEmailMsg, message) {
  return isEmailMsg
    ? JSON.stringify({ email: message })
    : JSON.stringify({ Text: message })
}



// WEBPACK FOOTER //
// ./src/state/actions/conversations-messages.js